import { SECTION_CARD_XML, SECTION_HERO_XML } from '../../../shared/onboardingSectionArt.js'
import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import Prose from '../components/ds/Prose'
import FaqAccordion from '../components/ds/FaqAccordion'
import { getCoverJsonLd } from '../lib/landingJsonLd'
import { dashboardUrl } from '../lib/dashboardUrl'

// /business/compliance — the page a practice manager forwards to their principal.
//
// ⛔⛔ THIS PAGE USED TO PUBLISH OUR CONTROLS, AND THAT WAS A SECURITY PROBLEM,
// not only a tone one (founder 2026-10-02).
//
// It carried a role by role table of exactly which documents each role must
// hold, named every check in the stack, and said how long each window lasts.
// Read by the right person that is a specification: the minimum set to forge,
// and the gap to aim at. A marketing page's job is to make a workplace confident
// that somebody competent is doing this. It is not to hand anybody the method.
//
// ⛔ So: no document lists, no named checks, no windows, no durations. Say what
// the workplace GETS. If a principal wants the detail before signing off, that
// is a conversation, and the page offers one.
//
// ⛔ And no time estimates anywhere, per the house copy rule.

const OUTCOMES = [
  {
    art: SECTION_CARD_XML.account,
    title: 'They are who they say they are',
    body: 'Nobody reaches your door on a name and a phone number. Identity is established before anyone can accept a shift with you.',
  },
  {
    art: SECTION_CARD_XML.business,
    title: 'They are allowed to do the job',
    body: 'Registration and the right to work are confirmed with the bodies that hold them, not taken on trust from a photocopy.',
  },
  {
    art: SECTION_CARD_XML.signing,
    title: 'And they still are on the day',
    body: 'A clearance that was true in March is not evidence in October. We keep looking, so nothing lapses quietly between shifts.',
  },
]

const FAQS = [
  { q: 'What do we have to check ourselves?',
    a: 'Nothing is hidden from you. You can see that everyone booked with you is cleared for the role before they arrive, and you can export that record whenever you need it.' },
  { q: 'What happens if someone stops being eligible?',
    a: 'They stop being able to accept work in that role. You do not have to notice, and you do not have to chase it.' },
  { q: 'Can we see their documents?',
    a: 'You see that somebody is cleared for the role on the day they work with you. That is the point of a shared check: nobody is emailing passport scans around.' },
  { q: 'Are they insured?',
    a: 'Clinical roles carry their own professional indemnity, and we confirm it.' },
  { q: 'Is TempDash an employment agency?',
    a: 'No. They are self employed professionals accepting work directly from you. Nobody joins your payroll and there is no supply relationship.' },
  { q: 'Our principal wants the detail before we sign off.',
    a: 'Good. Ask us and we will walk them through it properly, including what we hold and what you can export for an inspection. That conversation belongs with a person, not on a public page.' },
]

export default function CoverCompliance() {
  return (
    <div className="bg-canvas">
      <Seo
        title="Everyone who walks in is cleared"
        description="Know that whoever turns up is who they say they are, allowed to do the job, and still allowed to on the day. Without you chasing a single document."
        path="/business/compliance"
        jsonLd={getCoverJsonLd(FAQS, [['Get cover', '/business'], ['Compliance', '/business/compliance']])}
      />
      <MarketingNav />

      <main id="main" tabIndex={-1} className="outline-none">
        <PageHero
          art={SECTION_HERO_XML.verified}
          eyebrow="Get cover"
          title="Whoever walks in is cleared to be there."
          lead="You stop being the last line of defence. That is the whole of what this page is about."
        >
          <Button href={dashboardUrl('/register')}>Post a shift</Button>
        </PageHero>

        <Section ground="canvas">
          <SectionHead eyebrow="What you get" title={<>Three things you never have to wonder about again.</>} />
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {OUTCOMES.map((o) => (
              <div key={o.title}>
                <Art xml={o.art} className="h-[72px] w-[72px]" />
                <h3 className="mt-4 text-[21px] font-semibold">{o.title}</h3>
                <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{o.body}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section ground="navy">
          <div className="max-w-[760px]">
            <SectionHead on="navy" eyebrow="The part that matters" title={<>Checked before they can accept. Not after they arrive.</>} />
            <p className="mt-6 text-[19px] leading-relaxed text-white/75">
              Anyone who cannot be cleared never reaches your rota in the first place. You are not
              reviewing paperwork on the morning, and you are not finding out afterwards.
            </p>
          </div>
        </Section>

        <Section ground="surface" width="text">
          <div className="space-y-12">
            <Prose id="records" title="What you can show an inspector" art={ICON.compliance}>
              <p>
                For every shift: who worked it, what they are registered as, and that they were
                cleared on the day. Exportable, so it sits on your file and not only on ours.
              </p>
            </Prose>

            <Prose id="not-an-agency" title="Nobody joins your payroll" art={ICON.star}>
              <p>
                They are self employed and they contract with you directly. There is no supply
                relationship, nothing to run through payroll, and no agency in the middle.
              </p>
              <p>
                The terms are in our <a className="text-link underline underline-offset-4" href="/legal/terms">terms of business</a>.
              </p>
            </Prose>

            <Prose id="ask" title="Want to go deeper before you sign off?" art={ICON.support}>
              <p>
                Ask us. We would rather take a principal through it properly than publish a
                checklist on the open web, and you will get a straight answer the same day.
              </p>
              <p>
                <a className="text-link underline underline-offset-4" href="mailto:support@tempdash.app">support@tempdash.app</a>
              </p>
            </Prose>
          </div>
        </Section>

        <Section ground="surface" width="text">
          <H2>Questions, answered.</H2>
          <FaqAccordion items={FAQS} className="mt-10" />
        </Section>

          <CtaBand title="Cover you can sign off on.">
          <Button href={dashboardUrl('/register')}>Post a shift</Button>
        </CtaBand>
      </main>

      <MarketingFooter />
    </div>
  )
}
